'use client';

import React, { useMemo, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { Exchange, Segment } from '@/types/symbols/exchangeTypes';
import { useExchangeWebSocket } from '@/hooks/symbol/useExchangeWebSocket';
import { getPrice } from '@/utils/symbols/exchangeUtils';
import SegmentTabs from '@/components/(postTypes)/symbols/single/coin/exchange-live/SegmentTabs';
import ArbitrageProfitDisplay from '@/components/(postTypes)/symbols/single/coin/exchange-live/ArbitrageProfitDisplay';
import ExchangeRow from '@/components/(postTypes)/symbols/single/coin/exchange-live/ExchangeRow';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toPersianDigits } from '@/utils/persianUtils';

interface SingleCoinExchangesListProps {
    name_fa: string;
    symbol: string;
    type: number;
    initialExchangeList?: Exchange[];
}

const SingleCoinExchangesListLive: React.FC<SingleCoinExchangesListProps> = ({
    symbol,
    initialExchangeList = [],
}) => {
    const [segment, setSegment] = useState<Segment>('buy');

    const { exchanges, priceFlashes, arbitrageProfit, isUSDT, profitDirection } =
        useExchangeWebSocket(symbol, initialExchangeList, segment);

    const sorted = useMemo(() => {
        const arr = [...exchanges];
        arr.sort((a, b) => {
            const ap = getPrice(a, segment);
            const bp = getPrice(b, segment);
            return segment === 'buy' ? ap - bp : bp - ap;
        });
        return arr;
    }, [exchanges, segment]);

    const bestPrice = useMemo(() => {
        if (!sorted.length) return null;
        const prices = sorted.map((ex) => getPrice(ex, segment)).filter((p) => p > 0);
        if (!prices.length) return null;
        return segment === 'buy' ? Math.min(...prices) : Math.max(...prices);
    }, [sorted, segment]);

    return (
        <div className='w-full'>
            {/* Tablet/desktop: arbitrage sits beside the tabs (saves a row); phones: a strip on top of the table */}
            <div className='sm:flex sm:items-center sm:justify-between sm:gap-3'>
                <SegmentTabs segment={segment} onSegmentChange={setSegment} />
                {isUSDT && (
                    <div className='max-sm:hidden mb-3'>
                        <ArbitrageProfitDisplay
                            profit={arbitrageProfit}
                            direction={profitDirection}
                            variant='inline'
                        />
                    </div>
                )}
            </div>

            <div className='mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white/80 shadow-xs backdrop-blur-xs dark:border-white/10 dark:bg-primary-dark/60'>
                {isUSDT && (
                    <div className='sm:hidden'>
                        <ArbitrageProfitDisplay
                            profit={arbitrageProfit}
                            direction={profitDirection}
                        />
                    </div>
                )}
                {/* Phones: no # / volume columns (volume sits under the price), so nothing scrolls sideways */}
                <Table className='w-full'>
                    <TableHeader>
                        <TableRow className='text-[11px] sm:text-xs bg-slate-100/70 dark:bg-white/5 border-b border-gray-100 dark:border-white/10'>
                            <TableHead className='max-sm:hidden w-[36px] min-w-[36px] text-center font-bold whitespace-nowrap px-2'>
                                #
                            </TableHead>
                            <TableHead className='text-right font-bold whitespace-nowrap ps-3 sm:ps-2'>
                                صرافی ({toPersianDigits(sorted.length)})
                            </TableHead>
                            <TableHead className='text-right font-bold whitespace-nowrap'>
                                {segment === 'buy' ? 'قیمت خرید' : 'قیمت فروش'}
                                <span className='font-normal'> (تومان)</span>
                            </TableHead>
                            <TableHead
                                className='max-sm:hidden text-right font-bold whitespace-nowrap'
                                title='بیشترین مقداری که صرافی در همین قیمت می‌فروشد یا می‌خرد'
                            >
                                حجم در این قیمت ({symbol.toUpperCase()})
                            </TableHead>
                            <TableHead className='text-center font-bold whitespace-nowrap w-[64px] sm:w-[90px] pe-3 sm:pe-2'>
                                {segment === 'buy' ? 'خرید' : 'فروش'}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <AnimatePresence initial={false}>
                            {sorted.map((ex, idx) => (
                                <ExchangeRow
                                    key={ex.slug}
                                    exchange={ex}
                                    index={idx}
                                    segment={segment}
                                    isBestPrice={
                                        bestPrice != null && getPrice(ex, segment) === bestPrice
                                    }
                                    flash={priceFlashes[ex.slug]}
                                />
                            ))}
                        </AnimatePresence>
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export default React.memo(SingleCoinExchangesListLive);
